import { useState } from 'react';
import { Accordion as MuiAccordion, AccordionSummary, AccordionDetails } from '@mui/material';
import styled from '@emotion/styled';
import { CaretDown } from '@phosphor-icons/react';
import { Text } from 'components/text';

export type AccordionProps = {
  id?: string;
  title: React.ReactNode;
  headerEndContent?: React.ReactNode;
  content: React.ReactNode;
  expanded?: boolean;
  onChange?: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  defaultExpanded?: boolean;
  variant?: 'default' | 'filled';
};

export const Accordion = ({
  id,
  title,
  headerEndContent,
  content,
  expanded: controlledExpanded,
  onChange,
  defaultExpanded = false,
  variant = 'filled',
}: AccordionProps) => {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);

  const isControlled = controlledExpanded !== undefined;
  const isExpanded = isControlled ? controlledExpanded : uncontrolledExpanded;

  const handleChange = (event: React.SyntheticEvent, newExpanded: boolean) => {
    if (!isControlled) {
      setUncontrolledExpanded(newExpanded);
    }
    onChange?.(event, newExpanded);
  };

  return (
    <StyledAccordion expanded={isExpanded} onChange={handleChange} disableGutters elevation={0}>
      <StyledAccordionSummary
        variant={variant}
        expandIcon={<CaretDown size={20} />}
        aria-controls={`${id}-content`}
        id={`${id}-header`}
      >
        <SummaryContent>
          <TitleSection>
            <CaretIcon $expanded={isExpanded}>
              <CaretDown size={16} weight="bold" />
            </CaretIcon>
            <Text variant="labelSm" weight="semiBold" color="secondary" style={{ width: '100%' }}>
              {title}
            </Text>
          </TitleSection>
          {headerEndContent}
        </SummaryContent>
      </StyledAccordionSummary>
      <StyledAccordionDetails>{content}</StyledAccordionDetails>
    </StyledAccordion>
  );
};

const StyledAccordion = styled(MuiAccordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.border.primarySubtle}`,
  borderRadius: '8px',
  '&.MuiAccordion-root:first-of-type': {
    borderRadius: '8px',
  },
  '&.MuiAccordion-root:last-of-type': {
    borderRadius: '8px',
  },
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 0,
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderRadius: '8px',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)<{ variant: 'default' | 'filled' }>(
  ({ theme, variant }) => ({
    padding: `${theme.s.sm} ${theme.s.md}`,
    minHeight: 'auto',
    borderRadius: '8px',
    ...(variant === 'filled'
      ? {
          backgroundColor: theme.palette.bg.brandSubtle,
        }
      : {}),
    '&.Mui-expanded': {
      borderRadius: '8px 8px 0 0',
      minHeight: 'auto',
      borderBottom: `1px solid ${theme.palette.border.primarySubtle}`,
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
      '&.Mui-expanded': {
        margin: 0,
      },
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      display: 'none',
    },
  })
);

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: `${theme.s.lg}`,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.s.md,
}));

const SummaryContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${p => p.theme.s.xs};
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.s.xs};
  max-height: 22px;
  flex: 1;
`;

export const CaretIcon = styled.div<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
  transform: ${p => (p.$expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  color: ${p => p.theme.palette.fg.primary};
  cursor: pointer;
`;
